import { Child529Plan, FinancialPlanData, FinancialPlanProfile } from '../types/financialPlan';

/** ISO calendar date `YYYY-MM-DD` (local date, no time). */
export type ISODateString = string;

export function formatISODateLocal(d: Date): ISODateString {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseISODateLocal(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return date;
}

export function isValidISODate(value: unknown): value is ISODateString {
  return typeof value === 'string' && parseISODateLocal(value) != null;
}

/** Completed years since birth as of a calendar date. */
export function ageAtDate(birthDate: ISODateString, asOf: Date): number {
  const birth = parseISODateLocal(birthDate);
  if (!birth) return 0;
  let age = asOf.getFullYear() - birth.getFullYear();
  const monthDiff = asOf.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && asOf.getDate() < birth.getDate())) {
    age -= 1;
  }
  return Math.max(0, age);
}

/** Approximate birth date for a given age on an anchor date (same month/day as anchor). */
export function birthDateFromAge(age: number, asOf: Date): ISODateString {
  const clamped = Math.max(0, Math.floor(age));
  const birth = new Date(asOf.getFullYear() - clamped, asOf.getMonth(), asOf.getDate());
  return formatISODateLocal(birth);
}

export function planAsOfDate(profile: FinancialPlanProfile, fallback: Date = new Date()): Date {
  if (profile.planStartDate && isValidISODate(profile.planStartDate)) {
    const start = parseISODateLocal(profile.planStartDate)!;
    return fallback >= start ? fallback : start;
  }
  return fallback;
}

/** Calendar year for a plan age on the timeline. */
export function calendarYearForPlanAge(profile: FinancialPlanProfile, age: number): number {
  if (profile.birthDate && isValidISODate(profile.birthDate)) {
    const birth = parseISODateLocal(profile.birthDate)!;
    return birth.getFullYear() + age;
  }

  const anchor =
    profile.planStartDate && isValidISODate(profile.planStartDate)
      ? parseISODateLocal(profile.planStartDate)!
      : new Date();
  const ageAtAnchor =
    profile.birthDate && isValidISODate(profile.birthDate)
      ? ageAtDate(profile.birthDate, anchor)
      : profile.currentAge;

  return anchor.getFullYear() + (age - ageAtAnchor);
}

/**
 * Derive current ages from birth dates as of today (or plan start if still in the future).
 * Stored ages remain the fallback when no birth date is set.
 */
export function resolvePlanAges(plan: FinancialPlanData, asOf: Date = new Date()): FinancialPlanData {
  const effectiveAsOf = planAsOfDate(plan.profile, asOf);
  const profile = { ...plan.profile };

  if (profile.birthDate && isValidISODate(profile.birthDate)) {
    profile.currentAge = ageAtDate(profile.birthDate, effectiveAsOf);
  }
  if (profile.spouseBirthDate && isValidISODate(profile.spouseBirthDate)) {
    profile.spouseAge = ageAtDate(profile.spouseBirthDate, effectiveAsOf);
  }

  const children = plan.children.map((child) => {
    if (child.birthDate && isValidISODate(child.birthDate)) {
      return { ...child, currentAge: ageAtDate(child.birthDate, effectiveAsOf) };
    }
    return child;
  });

  return { ...plan, profile, children };
}

export function formatDisplayDate(iso: ISODateString): string {
  const d = parseISODateLocal(iso);
  if (!d) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function ageLabelFromBirthDate(
  birthDate: ISODateString | undefined,
  fallbackAge: number,
  asOf: Date = new Date(),
): string {
  if (birthDate && isValidISODate(birthDate)) {
    return String(ageAtDate(birthDate, asOf));
  }
  return String(fallbackAge);
}
